/**
 * DashboardHub — the redesigned financial dashboard (mockup build).
 * Replaces CommandCenter at /dashboard. Old dashboard kept at /dashboard-classic.
 * All numbers come from the accounting ledger via /api/dashboard-hub/* endpoints.
 *
 * Personalization: each user can hide dashboard cards they don't use via the
 * "Customize" menu. Preferences persist per-user in localStorage (device-local).
 */
import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import KpiGrid from '../components/dashboard-hub/KpiGrid';
import CashFlowChart from '../components/dashboard-hub/CashFlowChart';
import { ProfitLossSummary, TopExpenses, InvoiceOverview } from '../components/dashboard-hub/FinanceCards';
import { RecentActivity, AiAssistantCard, QuickActions, AiInsightBanner } from '../components/dashboard-hub/RightRail';
import { useHubRanges } from '../components/dashboard-hub/DateRangeSelect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import { SlidersHorizontal, RotateCcw } from 'lucide-react';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

// Cards the user is allowed to show/hide, in menu order.
const CARD_REGISTRY = [
  { id: 'kpis', label: 'Key metrics' },
  { id: 'cash_flow', label: 'Cash Flow Overview' },
  { id: 'profit_loss', label: 'Profit & Loss Summary' },
  { id: 'top_expenses', label: 'Top Expenses' },
  { id: 'invoices', label: 'Invoices Overview' },
  { id: 'insight', label: 'Weekly insight' },
  { id: 'recent_activity', label: 'Recent Activity' },
  { id: 'ai_assistant', label: 'AI Assistant' },
  { id: 'quick_actions', label: 'Quick Actions' },
];

const storageKey = (user) => `cb_dash_hidden_v1:${user?.id || user?.email || 'anon'}`;

function useHiddenCards(user) {
  const key = storageKey(user);
  const [hidden, setHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  const persist = useCallback((next) => {
    setHidden(next);
    try {
      localStorage.setItem(key, JSON.stringify(Array.from(next)));
    } catch {
      /* ignore quota/availability errors */
    }
  }, [key]);

  const toggle = useCallback((id) => {
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    persist(next);
  }, [hidden, persist]);

  const reset = useCallback(() => persist(new Set()), [persist]);

  return { isHidden: (id) => hidden.has(id), toggle, reset, hiddenCount: hidden.size };
}

export default function DashboardHub() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [ranges, updateRange] = useHubRanges(wsId);
  const firstName = (user?.name || user?.email || 'there').split(' ')[0].split('@')[0];
  const { isHidden, toggle, reset, hiddenCount } = useHiddenCards(user);

  // The four half-width finance cards, filtered to the visible ones so the
  // grid reflows cleanly (no empty gaps) when some are hidden.
  const financeCards = [
    isHidden('cash_flow') ? null : (
      <CashFlowChart key="cash_flow" wsId={wsId} range={ranges.cash_flow} onRangeChange={(r) => updateRange('cash_flow', r)} />
    ),
    isHidden('profit_loss') ? null : (
      <ProfitLossSummary key="profit_loss" wsId={wsId} range={ranges.profit_loss} onRangeChange={(r) => updateRange('profit_loss', r)} />
    ),
    isHidden('top_expenses') ? null : (
      <TopExpenses key="top_expenses" wsId={wsId} range={ranges.top_expenses} onRangeChange={(r) => updateRange('top_expenses', r)} />
    ),
    isHidden('invoices') ? null : (
      <InvoiceOverview key="invoices" wsId={wsId} range={ranges.invoices_overview} onRangeChange={(r) => updateRange('invoices_overview', r)} />
    ),
  ].filter(Boolean);

  const railVisible = !isHidden('recent_activity') || !isHidden('ai_assistant') || !isHidden('quick_actions');

  return (
    <div className="min-h-full bg-[#F8FAFC] px-4 py-6 sm:px-6" data-testid="dashboard-hub-page">
      {/* Greeting header */}
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#172033] sm:text-3xl" data-testid="dashboard-greeting">
            {greeting()}, {firstName}! <span aria-hidden="true">👋</span>
          </h1>
          <p className="mt-1 text-sm text-[#657085]">Here's what's happening with your business today.</p>
        </div>

        {/* Customize — show/hide cards so the dashboard feels personal */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="dashboard-customize-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm font-medium text-[#475467] shadow-sm transition-colors hover:bg-[#F1F5F9] hover:text-[#172033] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Customize</span>
              {hiddenCount > 0 && (
                <span className="rounded-full bg-emerald-500/15 px-1.5 text-xs font-semibold text-emerald-700" data-testid="dashboard-hidden-count">
                  {hiddenCount} hidden
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60" data-testid="dashboard-customize-menu">
            <DropdownMenuLabel>Show on dashboard</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {CARD_REGISTRY.map((card) => (
              <DropdownMenuCheckboxItem
                key={card.id}
                checked={!isHidden(card.id)}
                onCheckedChange={() => toggle(card.id)}
                onSelect={(e) => e.preventDefault()}
                data-testid={`dashboard-toggle-${card.id}`}
              >
                {card.label}
              </DropdownMenuCheckboxItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); reset(); }}
              className="text-[#475467]"
              data-testid="dashboard-reset-btn"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset to default
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* Main column */}
        <div className="min-w-0 space-y-5">
          {!isHidden('kpis') && <KpiGrid wsId={wsId} ranges={ranges} updateRange={updateRange} />}
          {financeCards.length > 0 && (
            <div className="grid gap-5 lg:grid-cols-2">{financeCards}</div>
          )}
          {!isHidden('insight') && <AiInsightBanner wsId={wsId} />}
        </div>

        {/* Right rail */}
        {railVisible && (
          <aside className="min-w-0 space-y-5">
            {!isHidden('recent_activity') && <RecentActivity wsId={wsId} />}
            {!isHidden('ai_assistant') && <AiAssistantCard />}
            {!isHidden('quick_actions') && <QuickActions role={currentWorkspace?.role} />}
          </aside>
        )}
      </div>
    </div>
  );
}
